import { useEffect, useRef, useState } from 'react';
import {
  FILTER_PRESETS,
  buildCSSFilter,
  DEFAULT_ADJUSTMENTS,
  type FilterPreset,
} from './imageEditorTypes';
import { useTranslation } from '../../i18n';
import styles from './FilterPresets.module.css';

interface FilterPresetsProps {
  imageSrc: string;
  activeFilter: string;
  onSelect: (preset: FilterPreset) => void;
}

const THUMB_W = 160;
const THUMB_H = 120;

export function FilterPresets({ imageSrc, activeFilter, onSelect }: FilterPresetsProps) {
  const { t } = useTranslation();
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Generate a single small thumbnail on mount
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_W;
      canvas.height = THUMB_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Cover crop (center)
      const scale = Math.max(THUMB_W / img.width, THUMB_H / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (THUMB_W - w) / 2, (THUMB_H - h) / 2, w, h);
      setThumbUrl(canvas.toDataURL('image/jpeg', 0.7));
      canvasRef.current = canvas;
    };
    img.src = imageSrc;
  }, [imageSrc]);

  if (!thumbUrl) return null;

  return (
    <div className={styles.grid}>
      {FILTER_PRESETS.map((preset) => {
        const filterCSS = buildCSSFilter({
          ...DEFAULT_ADJUSTMENTS,
          ...preset.adjustments,
        });
        return (
          <button
            key={preset.key}
            type="button"
            className={`${styles.preset} ${activeFilter === preset.key ? styles.presetActive : ''}`}
            onClick={() => onSelect(preset)}
          >
            <img
              src={thumbUrl}
              alt={t(preset.labelKey as Parameters<typeof t>[0])}
              className={styles.thumb}
              style={{ filter: filterCSS === 'none' ? undefined : filterCSS }}
            />
            <span className={styles.presetLabel}>
              {t(preset.labelKey as Parameters<typeof t>[0])}
            </span>
          </button>
        );
      })}
    </div>
  );
}
